/**
 * 深邃蓝模板 - 共享页脚
 * 显示备案号、公司信息、联系方式、底部菜单
 */

import { Link } from "react-router-dom";
import { useSiteConfig } from "@/context/SiteConfigContext";

export default function DeepBlueFooter() {
  const { siteConfig } = useSiteConfig();

  const phone = siteConfig?.contact_info?.phone || "";
  const email = siteConfig?.contact_info?.email || "";
  const address = siteConfig?.contact_info?.address;
  const companyName = siteConfig?.contact_info?.company_name;
  const icp = siteConfig?.icp || "";
  const icpUrl = siteConfig?.icp_url || "";
  const displayName = siteConfig?.site_name || "";
  const siteName = siteConfig?.name || "";
  const logo = siteConfig?.logo || "";
  const footerInfo = siteConfig?.footer_info || "";
  const wechat = siteConfig?.contact_info?.wechat || "";

  const defaultMenus = [
    { id: "m1", label: "首页", href: "/", external: false, enabled: true },
    { id: "m2", label: "发布信息", href: "/publish", external: false, enabled: true },
    { id: "m3", label: "开通VIP", href: "/vip", external: false, enabled: true },
    { id: "m4", label: "会员中心", href: "/dashboard", external: false, enabled: true },
    { id: "m5", label: "帮助中心", href: "/page/help", external: false, enabled: true },
    { id: "m6", label: "关于我们", href: "/page/about", external: false, enabled: true },
  ];

  const hasFooterMenus = siteConfig?.footer_menus && Array.isArray(siteConfig.footer_menus) && siteConfig.footer_menus.length > 0;
  const menus = hasFooterMenus
    ? siteConfig.footer_menus.filter((m: any) => m.enabled !== false)
    : defaultMenus;

  const currentYear = new Date().getFullYear();
  const copyrightOwner = companyName || displayName || siteName;
  const copyright = copyrightOwner ? `${currentYear} ${copyrightOwner} 版权所有` : `${currentYear} 版权所有`;
  const brandName = displayName || siteName;
  const quickLinks = menus.slice(0, 6);

  return (
    <footer className="bg-[#0A1628] text-gray-300 border-t border-blue-900/30">
      {/* 主体区域 */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">

          {/* 品牌介绍 */}
          <div>
            <div className="flex items-center gap-2.5 mb-3">
              {logo ? (
                <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center p-1 overflow-hidden">
                  <img src={logo} alt={brandName} className="w-full h-full object-contain brightness-0 invert" />
                </div>
              ) : brandName ? (
                <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center text-white text-xs font-bold">
                  {brandName.charAt(0)}
                </div>
              ) : null}
              {brandName && <span className="text-base font-bold text-white">{brandName}</span>}
            </div>
            {footerInfo ? (
              <p className="text-sm text-gray-400 leading-relaxed">{footerInfo}</p>
            ) : (
              <p className="text-sm text-gray-400 leading-relaxed">
                专业的商业信息服务平台，汇聚各类商业服务资源。
              </p>
            )}
          </div>

          {/* 快捷链接 */}
          <div>
            <h4 className="text-xs font-bold text-blue-400 uppercase tracking-wider mb-4">快捷链接</h4>
            <div className="grid grid-cols-2 gap-x-4 gap-y-2">
              {quickLinks.map((item: any) => (
                <div key={item.id}>
                  {item.external ? (
                    <a href={item.href} target="_blank" rel="nofollow noopener noreferrer"
                      className="text-sm text-gray-400 hover:text-blue-400 transition-colors">
                      {item.label}
                    </a>
                  ) : (
                    <Link to={item.href} className="text-sm text-gray-400 hover:text-blue-400 transition-colors">
                      {item.label}
                    </Link>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* 联系我们 */}
          <div>
            <h4 className="text-xs font-bold text-blue-400 uppercase tracking-wider mb-4">联系我们</h4>
            <ul className="space-y-3">
              {companyName && (
                <li className="flex items-center gap-2.5 text-sm text-gray-400">
                  <div className="w-8 h-8 rounded-lg bg-blue-900/30 flex items-center justify-center shrink-0">
                    <i className="ri-building-line text-blue-400" />
                  </div>
                  {companyName}
                </li>
              )}
              {phone && (
                <li>
                  <a href={`tel:${phone}`} className="flex items-center gap-2.5 text-sm text-gray-400 hover:text-blue-400 transition-colors">
                    <div className="w-8 h-8 rounded-lg bg-blue-900/30 flex items-center justify-center shrink-0">
                      <i className="ri-phone-line text-blue-400" />
                    </div>
                    {phone}
                  </a>
                </li>
              )}
              {email && (
                <li>
                  <a href={`mailto:${email}`} className="flex items-center gap-2.5 text-sm text-gray-400 hover:text-blue-400 transition-colors">
                    <div className="w-8 h-8 rounded-lg bg-blue-900/30 flex items-center justify-center shrink-0">
                      <i className="ri-mail-line text-blue-400" />
                    </div>
                    {email}
                  </a>
                </li>
              )}
              {address && (
                <li className="flex items-center gap-2.5 text-sm text-gray-400">
                  <div className="w-8 h-8 rounded-lg bg-blue-900/30 flex items-center justify-center shrink-0">
                    <i className="ri-map-pin-line text-blue-400" />
                  </div>
                  {address}
                </li>
              )}
              {!phone && !email && !address && !companyName && (
                <li className="text-sm text-gray-500">暂无联系方式</li>
              )}
            </ul>
          </div>
        </div>
      </div>

      {/* 底栏 */}
      <div className="border-t border-blue-900/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-2">
          <span className="text-xs text-gray-500">{copyright}</span>
          {icp && (
            <a href={icpUrl} target="_blank" rel="nofollow noopener noreferrer"
              className="text-xs text-gray-500 hover:text-blue-400 transition-colors">
              {icp}
            </a>
          )}
        </div>
      </div>
    </footer>
  );
}
